import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toDeleteBlockedError } from '@/lib/errors/deleteBlockedError';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { RiskRating } from './useRiskRatings';

import type { InvestmentType, InvestmentAccountWithType } from '@/integrations/supabase/investment.models';

export type { InvestmentType };

/** An investment account with its type and risk rating, as the investment lists select them. */
export type InvestmentAccount = InvestmentAccountWithType & {
  risk_rating?: RiskRating;
};

export interface InvestmentFormData {
  investment_type_id: string;
  fund_name: string;
  fund_manager: string;
  account_number: string;
  currency: string;
  interest_rate: number;
  starting_amount: number;
  notes?: string;
  description?: string;
  member_number?: string;
  risk_rating_id?: string;
}

/**
 * Hook to fetch all investment accounts with joined investment type data
 * 
 * @returns Query result containing investment accounts with investment_type data
 * 
 * @example
 * const { data: investments, isLoading } = useInvestments();
 */
export function useInvestments() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['investments', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('investment_accounts' as any)
        .select('*, investment_type:investment_types(*), risk_rating:risk_ratings(*)')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as unknown as InvestmentAccount[];
    },
    enabled: !!user,
  });
}

/**
 * Hook to create a new investment account
 * 
 * @returns Mutation function to create investment account
 * 
 * @example
 * const createInvestment = useCreateInvestment();
 * await createInvestment.mutateAsync(formData);
 */
export function useCreateInvestment() {
  const qc = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (investment: InvestmentFormData) => {
      const typeId = investment.investment_type_id;
      
      // Create investment account with starting_amount as initial balance
      const { ...accountData } = investment;
      const { data, error } = await supabase
        .from('investment_accounts' as any)
        .insert({
          ...accountData,
          investment_type_id: typeId,
          balance: investment.starting_amount,
          user_id: user!.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as unknown as InvestmentAccount;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['investments'] });
      qc.invalidateQueries({ queryKey: ['investment-types'] });
    },
  });
}

/**
 * Hook to update an existing investment account
 * 
 * @returns Mutation function to update investment account
 * 
 * @example
 * const updateInvestment = useUpdateInvestment();
 * await updateInvestment.mutateAsync({ id: '123', fund_name: 'New Name' });
 */
export function useUpdateInvestment() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<InvestmentAccount> & { id: string }) => {
      const { data, error } = await supabase
        .from('investment_accounts' as any)
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as unknown as InvestmentAccount;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['investments'] });
      qc.invalidateQueries({ queryKey: ['investment-account', data.id] });
    },
  });
}

/**
 * Hook to delete an investment account
 * 
 * @returns Mutation function to delete investment account
 * 
 * @example
 * const deleteInvestment = useDeleteInvestment();
 * await deleteInvestment.mutateAsync('investment-id');
 */
export function useDeleteInvestment() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('investment_accounts' as any)
        .delete()
        .eq('id', id);
      
      if (error) {
        throw toDeleteBlockedError(error, "This investment account has transactions, so it can't be deleted.");
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['investments'] }),
  });
}
